import {Component, OnInit} from '@angular/core';
import {AccountService, AlertService, GroupService, UserService} from "../services";
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';

import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';

import { locale as polish } from './i18n/pl';
import { locale as english } from './i18n/en';

import {ComponentTitleService} from "../services/componentTitle.service";
import {FuseTranslationLoaderService} from "../../../@fuse/services/translation-loader.service";
import {CountryService} from "../services/country.service";
import {Country} from "../models/country";
import {Service} from "../models/service";
import {User} from "../models";

@Component({
    templateUrl: './newUser.component.html',
    styleUrls: ['./newUser.component.scss'],
})

export class NewUserComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    cardNumber: string;
    isAddMode: boolean;
    roles = [];
    countries: Country[];
    services: Service[];
    userData: User;

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private alertService: AlertService,
        private accountService: AccountService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private countryService: CountryService
    ) {
        componentTitleService.clearTitle();
        componentTitleService.addPart('Workers');

        this.translationLoader.loadTranslations(polish, english);

        if (accountService.hasRole('ROLE_SUPER_ADMIN')) {
            this.roles.push('ROLE_SUPER_ADMIN');
            this.roles.push('ROLE_ADMIN');
            this.roles.push('ROLE_MANAGER');
            this.roles.push('ROLE_RECEPTIONIST');
            this.roles.push('ROLE_WORKER');
            this.roles.push('ROLE_TRAINER');
        } else if (accountService.hasRole('ROLE_ADMIN')) {
            this.roles.push('ROLE_MANAGER');
            this.roles.push('ROLE_RECEPTIONIST');
            this.roles.push('ROLE_WORKER');
            this.roles.push('ROLE_TRAINER');
        } else if (accountService.hasRole('ROLE_MANAGER')) {
            this.roles.push('ROLE_RECEPTIONIST');
            this.roles.push('ROLE_WORKER');
            this.roles.push('ROLE_TRAINER');
        }
    }

    ngOnInit() {
        this.id = this.route.snapshot.params['id'];
        this.cardNumber = this.route.snapshot.params['cardNumber']?this.route.snapshot.params['cardNumber']:'';
        this.isAddMode = !this.id;

        this.countries = this.route.snapshot.data.countries;
        this.services = this.route.snapshot.data.services.items;

        // password not required in edit mode
        const passwordValidators = [Validators.minLength(6)];
        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
            passwordValidators.push(Validators.required);
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: ['', Validators.required],
            plainPassword: ['', passwordValidators],
            street: [''],
            houseNumber: [''],
            flatNumber: [''],
            postCode: [''],
            city: [''],
            country: [''],
            phone: [''],
            roles: ['', Validators.required],
            services: [[]],
            calendarPriority: ['', Validators.required],
            visibleInCalendar: [false],
            visibleInScheduler: [false],
        });

        this.userData = new User();

        if (!this.isAddMode) {
            this.userData = this.route.snapshot.data.userData;
            this.f.firstName.setValue(this.userData.firstName);
            this.f.lastName.setValue(this.userData.lastName);
            this.f.email.setValue(this.userData.email);
            this.f.phone.setValue(this.userData.phone);
            this.f.street.setValue(this.userData.street);
            this.f.houseNumber.setValue(this.userData.houseNumber);
            this.f.flatNumber.setValue(this.userData.flatNumber);
            this.f.postCode.setValue(this.userData.postCode);
            this.f.city.setValue(this.userData.city);
            this.f.country.setValue(this.userData.country);
            this.f.roles.setValue(this.userData.roles);
            this.f.services.setValue(this.userData.services);
            this.f.calendarPriority.setValue(this.userData.calendarPriority);
            this.f.visibleInCalendar.setValue(this.userData.visibleInCalendar);
            this.f.visibleInScheduler.setValue(this.userData.visibleInScheduler);
        }
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createUser();
        } else {
            this.updateUser();
        }
    }

    private createUser() {
        let data = this.form.value;
        let services = [];

        for (let i=0; i<data.services.length; i++) {
            services.push({id: data.services[i].id});
        }

        data.services = services;

        this.userService.create(this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('User added successfully', { keepAfterRouteChange: true });

                    if (!this.form.value.roles.includes('ROLE_TRAINER')) {
                        this.router.navigate(['.', {relativeTo: this.route}]);
                    } else {
                        this.router.navigate(['trainers']);
                    }
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }

    private updateUser() {
        let data = this.form.value;
        let services = [];

        for (let i=0; i<data.services.length; i++) {
            services.push({id: data.services[i].id});
        }

        data.services = services;

        this.userService.update(this.id, this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Update successful', { keepAfterRouteChange: true });
                    this.router.navigate(['..', { relativeTo: this.route }]);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }

    compareStrings(o1: any, o2: any): boolean {
        return o1 === o2;
    }

    compareObjects(o1: any, o2: any): boolean {
        return o1.name === o2.name && o1.id === o2.id;
    }
}
